import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ProblemChartProps {
  selectedProblem: string;
}

const ProblemChart: React.FC<ProblemChartProps> = ({ selectedProblem }) => {
  const chartRef = useRef<ChartJS>(null);

  useEffect(() => {
    // When the selected problem changes, animate the chart
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [selectedProblem]);

  if (!selectedProblem) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 mt-8 flex items-center justify-center text-gray-500 h-[300px]">
        <p className="text-lg">Wählen Sie eine Herausforderung, um die Daten zu sehen</p>
      </div>
    );
  }

  // Different chart types and data based on the selected problem
  switch (selectedProblem) {
    case 'Krankheitsausfälle reduzieren':
      const barData = {
        labels: ['Ohne System', 'Mit System'],
        datasets: [
          {
            label: 'Durchschnittliche Krankheitstage pro Mitarbeiter pro Jahr',
            data: [12.2, 8.5],
            backgroundColor: ['rgba(239, 68, 68, 0.7)', 'rgba(16, 185, 129, 0.7)'],
            borderColor: ['rgb(239, 68, 68)', 'rgb(16, 185, 129)'],
            borderWidth: 1,
          },
        ],
      };

      const barOptions: ChartOptions<'bar'> = {
        responsive: true,
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Krankheitstage'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Implementierungsstatus'
            }
          }
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Reduzierung der Krankheitstage',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return `${context.dataset.label}: ${context.parsed.y} Tage`;
              }
            }
          }
        },
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <Bar data={barData} options={barOptions} ref={chartRef} />
          <div className="mt-4 text-sm text-gray-600">
            <p>Mit einem implementierten Gesundheits-Benefit-System können Unternehmen durchschnittlich 30% weniger Krankheitstage verzeichnen.</p>
          </div>
        </div>
      );

    case 'Mitarbeitende binden':
      const pieData = {
        labels: ['Gebundene Mitarbeiter', 'Fluktuation ohne System'],
        datasets: [
          {
            data: [85, 15],
            backgroundColor: [
              'rgba(16, 185, 129, 0.7)',
              'rgba(239, 68, 68, 0.7)',
            ],
            borderColor: [
              'rgb(16, 185, 129)',
              'rgb(239, 68, 68)',
            ],
            borderWidth: 1,
          },
        ],
      };

      const pieOptions: ChartOptions<'pie'> = {
        responsive: true,
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Mitarbeiterbindung mit Benefit-System',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return `${context.label}: ${context.parsed}%`;
              }
            }
          }
        },
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <div className="max-w-md mx-auto">
            <Pie data={pieData} options={pieOptions} ref={chartRef} />
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p>Unternehmen mit emotionaler Mitarbeiterbindung durch Benefit-Systeme erreichen Bindungsraten von bis zu 85%, verglichen mit 65% bei Unternehmen ohne diese Systeme.</p>
          </div>
        </div>
      );

    case 'Top-Talente gewinnen':
      const lineData = {
        labels: ['Start', 'Nach 6 Monaten', 'Nach 1 Jahr', 'Nach 2 Jahren'],
        datasets: [
          {
            label: 'Qualifizierte Bewerbungen mit Benefit-System',
            data: [10, 18, 27, 42],
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.5)',
            tension: 0.3,
          },
          {
            label: 'Qualifizierte Bewerbungen ohne System',
            data: [10, 12, 15, 18],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.5)',
            tension: 0.3,
          },
        ],
      };

      const lineOptions: ChartOptions<'line'> = {
        responsive: true,
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        },
        scales: {
          y: {
            beginAtZero: false,
            title: {
              display: true,
              text: 'Durchschnittliche Anzahl qualifizierter Bewerbungen pro Monat'
            }
          },
          x: {
            title: {
              display: true,
              text: 'Zeitraum'
            }
          }
        },
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Entwicklung der Bewerberqualität',
          }
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <Line data={lineData} options={lineOptions} ref={chartRef} />
          <div className="mt-4 text-sm text-gray-600">
            <p>Unternehmen mit einem attraktiven Gesundheits-Benefit-System verzeichnen nach 2 Jahren mehr als doppelt so viele qualifizierte Bewerbungen wie vergleichbare Unternehmen ohne entsprechende Systeme.</p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default ProblemChart;